import { useEffect, useState } from 'react';
import { Form, Select, DatePicker, Button, Row, Col, message } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { ChartFilter } from './filter-type';
import { Header } from 'src/screens/board/board.style';
import { ChartComponent } from '../chart/ChartComponent';
import PatrimoniesReportTable from '../chart/PatrimoniesReportTable';
import OperatorsReportTable from '../chart/OperatorsReportTable';
import { exportWorkshopReportCsv } from '../export-workshop-report';

const { RangePicker } = DatePicker;

const groupByOptions = [
  { value: 'sector', label: 'Setor' },
  { value: 'cell', label: 'Célula' },
  { value: 'patrimony', label: 'Máquina' },
  { value: 'operator', label: 'Operador' },
  { value: 'technician', label: 'Técnico' },
];

const periodTypeOptions = [
  { value: 'occurredAt', label: 'Aberto' },
  { value: 'finishedAt', label: 'Finalizado' },
];

const maintenanceTypeOptions = [
  { value: 0, label: 'OS Corretiva' },
  { value: 1, label: 'OS Preventiva' },
];

interface FilterComponentProps {
  filters: ChartFilter;
  onChange: (filters: ChartFilter) => void;
  sectors: { label: string; value: number }[];
  cells: { label: string; value: number }[];
  patrimonies: { label: string; value: number }[];
  technicians: { label: string; value: number }[];
  operators: { label: string; value: number }[];
}

export default function FilterComponent({
  filters,
  onChange,
  sectors,
  cells,
  patrimonies,
  technicians,
  operators,
}: FilterComponentProps) {
  const [form] = Form.useForm();
  const [activeReport, setActiveReport] = useState<'tasks' | 'operators'>('tasks');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const startDate = filters.startDate ? dayjs(filters.startDate) : null;
    const endDate = filters.endDate ? dayjs(filters.endDate) : null;

    const dateRange =
      startDate && endDate && dayjs.isDayjs(startDate) && dayjs.isDayjs(endDate)
        ? [startDate, endDate]
        : [];

    form.setFieldsValue({
      dateRange,
      groupBy: filters.groupBy,
      periodType: filters.periodType,
      sectorId: filters.sectorId,
      cellId: filters.cellId,
      patrimonyId: filters.patrimonyId,
      technicianId: filters.technicianId,
      operatorId: filters.operatorId,
      maintenanceType: filters.maintenanceType,
    });
  }, [filters, form]);

  const onFinish = (values: any) => {
    const [start, end] = values.dateRange || [];
    onChange({
      startDate: start ? start.format('YYYY-MM-DD') : '',
      endDate: end ? end.format('YYYY-MM-DD') : '',
      groupBy: values.groupBy,
      periodType: values.periodType,
      sectorId: values.sectorId || '',
      cellId: values.cellId || '',
      patrimonyId: values.patrimonyId || '',
      technicianId: values.technicianId || '',
      operatorId: values.operatorId || '',
      maintenanceType:
        values.maintenanceType === 0 || values.maintenanceType === 1
          ? values.maintenanceType
          : ('' as any),
    });
  };

  const commonSelectProps = {
    allowClear: true,
    showSearch: true,
    optionFilterProp: 'children',
    filterOption: (input: string, option?: { label: string; value: any }) =>
      (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      await exportWorkshopReportCsv(activeReport, filters);
    } catch (error) {
      console.error('Error exporting workshop report', error);
      message.error('Não foi possível exportar o relatório em CSV.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Header>
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        initialValues={{
          groupBy: 'technician',
          periodType: 'finishedAt',
          dateRange: [dayjs().startOf('month'), dayjs().endOf('month')],
        }}
      >
        <Row gutter={[16, 8]}>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item
              name="dateRange"
              label="Período"
              rules={[{ required: true, message: 'Selecione o período!' }]}
            >
              <RangePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                allowEmpty={[false, false]}
                disabledDate={(current) => current && current > dayjs().endOf('day')}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item
              name="periodType"
              label="Tipo de Período"
              rules={[{ required: true }]}
            >
              <Select
                {...commonSelectProps}
                style={{ width: '100%' }}
                options={periodTypeOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item
              name="groupBy"
              label="Agrupar por"
              rules={[{ required: true }]}
            >
              <Select
                {...commonSelectProps}
                style={{ width: '100%' }}
                options={groupByOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item name="sectorId" label="Setor">
              <Select
                {...commonSelectProps}
                style={{ width: '100%' }}
                placeholder="Setor"
                options={sectors}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item name="cellId" label="Célula">
              <Select
                {...commonSelectProps}
                style={{ width: '100%' }}
                placeholder="Célula"
                options={cells}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item name="patrimonyId" label="Máquina">
              <Select
                {...commonSelectProps}
                style={{ width: '100%' }}
                placeholder="Máquina"
                options={patrimonies}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item name="technicianId" label="Técnico">
              <Select
                {...commonSelectProps}
                style={{ width: '100%' }}
                placeholder="Técnico"
                options={technicians}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item name="operatorId" label="Operador">
              <Select
                {...commonSelectProps}
                style={{ width: '100%' }}
                placeholder="Operador"
                options={operators}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item name="maintenanceType" label="Tipo de OS">
              <Select
                {...commonSelectProps}
                style={{ width: '100%' }}
                placeholder="Todas"
                options={maintenanceTypeOptions}
              />
            </Form.Item>
          </Col>
          <Col
            xs={24}
            sm={12}
            md={8}
            lg={6}
            xl={4}
            style={{ display: 'flex', alignItems: 'flex-end' }}
          >
            <Form.Item style={{ width: '100%' }}>
              <Button type="primary" htmlType="submit" block>
                Filtrar
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <section style={{ marginTop: 32 }}>
        <h2>Gráfico de OS</h2>
        <ChartComponent filters={filters} />
      </section>

      <section style={{ marginTop: 64 }}>
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 16,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Button
            type={activeReport === 'tasks' ? 'primary' : 'default'}
            onClick={() => setActiveReport('tasks')}
          >
            OS por Máquina
          </Button>
          <Button
            type={activeReport === 'operators' ? 'primary' : 'default'}
            onClick={() => setActiveReport('operators')}
          >
            OS por Operador
          </Button>
          <Button
            loading={exporting}
            onClick={handleExportCsv}
            style={{
              backgroundColor: '#52c41a',
              borderColor: '#52c41a',
              color: '#fff',
            }}
          >
            Exportar CSV
          </Button>
        </div>

        {activeReport === 'tasks' && <PatrimoniesReportTable filters={filters} />}
        {activeReport === 'operators' && <OperatorsReportTable filters={filters} />}
      </section>
    </Header>
  );
}

